import React, { useEffect, useState, useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import api from "../utils/api";
import Breadcrumb from "./Breadcrumb";
import ChartBurgerMenu from "./ChartBurgerMenu";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";
import BoltLoader from "./BoltLoader";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";
import { getMachineColor } from "../utils/machineColors";
import {
  formatAmount,
  rateForCategory,
  resolveTariffs,
  unitLabel,
} from "../utils/currencyConversion";

const DrillDownPieChart = ({
  username,
  dateRange,
  onLoadingChange,
  scopeHierarchyPath,
  machineColors = {},
  currencyMode = "Energy",
  tariffs,
}) => {
  const isPkr = currencyMode === "PKR";
  // HistoricalTab passes the resolved set; fall back so the chart still works
  // if it is ever mounted standalone.
  const rates = useMemo(() => tariffs || resolveTariffs(null), [tariffs]);
  const valueUnit = unitLabel(isPkr);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState(null);
  const [history, setHistory] = useState([]);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const chartRef = useRef(null);

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzePieChart = () => {
    const title = currentView
      ? `${hierarchyData[currentView.name]?.machine_name || currentView.name} Breakdown`
      : "Energy Breakdown";

    const datasets = (chartData.labels || []).map((label, idx) => ({
      label: label,
      data: [chartData.series[idx] || 0],
    }));

    const payload = buildChartPayload({
      chartTitle: title,
      metricName: isPkr ? "Cost Breakdown" : "Energy Breakdown",
      unit: valueUnit,
      timestamps: [isPkr ? "Energy Cost" : "Energy Consumption"],
      datasets: datasets,
      dateRange: dateRange,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  const parseValue = (val) => {
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const num = parseFloat(val.replace(/[^\d.]/g, ""));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };

  const getNodeEnergy = (name) => {
    if (!hierarchyData || !hierarchyData[name]) return 0;
    return parseValue(hierarchyData[name].energy);
  };

  const resetToRoot = (data) => {
    const rootData = data || hierarchyData;
    if (!rootData) return;

    // Determine root nodes based on hierarchy_path depth
    const nodes = Object.values(rootData);
    const minPathLength = Math.min(
      ...nodes.map(
        (n) => (n.hierarchy_path || "").split("/").filter(Boolean).length,
      ),
    );
    const rootNodes = nodes.filter(
      (n) =>
        (n.hierarchy_path || "").split("/").filter(Boolean).length ===
        minPathLength,
    );

    // If there is only exactly 1 root node, jump directly to its children (2nd node by default)
    if (
      rootNodes.length === 1 &&
      rootNodes[0].children &&
      rootNodes[0].children.length > 0
    ) {
      const topNode = rootNodes[0];
      const initialView = {
        name: topNode.machine_name,
        children: topNode.children,
        isRoot: true,
      };

      setCurrentView(initialView);
      setHistory([]);
      setBreadcrumbPath([
        { label: topNode.machine_name, onClick: () => resetToRoot(rootData) },
      ]);
    } else {
      setCurrentView({
        name: "Main Supply",
        children: rootNodes,
        isRoot: true,
      });
      setHistory([]);
      setBreadcrumbPath([
        { label: "Main Supply", onClick: () => resetToRoot(rootData) },
      ]);
    }
  };

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      if (!username || !dateRange?.start || !dateRange?.end) return;

      setLoading(true);
      setError(null);

      try {
        const formatDateTime = (d) => {
          if (!d) return "";
          const dateObj = typeof d === "string" ? new Date(d) : d;
          if (isNaN(dateObj.getTime())) return d;

          // Format as YYYY-MM-DD HH:mm:ss for backend compatibility
          const isoString = new Date(
            dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
          ).toISOString();
          return isoString.replace("T", " ").slice(0, 19);
        };

        const response = await api.get("/energy-hierarchy-tree", {
          params: {
            username,
            start: formatDateTime(dateRange.start),
            end: formatDateTime(dateRange.end),
          },
        });

        let data = response.data;
        // Scope to the selected branch's subtree (Historical branch filter):
        // keep only nodes whose hierarchy_path is under the branch, so the
        // breakdown roots at the branch instead of the whole facility.
        if (scopeHierarchyPath && data && typeof data === "object") {
          const scoped = {};
          Object.entries(data).forEach(([k, v]) => {
            if ((v?.hierarchy_path || "").startsWith(scopeHierarchyPath)) scoped[k] = v;
          });
          data = scoped;
        }
        setHierarchyData(data);
        resetToRoot(data);
      } catch (err) {
        console.error("Error fetching hierarchy data:", err);
        setError("Failed to load energy data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, dateRange, scopeHierarchyPath]);

  const defaultPalette = [
    "#009f8d",
    "#ff9c07",
    "#00c6a9",
    "#4db6ac",
    "#ffb74d",
    "#26a69a",
    "#66bb6a",
    "#ffa726",
  ];

  const chartData = useMemo(() => {
    if (!currentView || !hierarchyData)
      return { series: [], labels: [], colors: [], items: [] };

    const items = currentView.children.filter(
      (node) => parseValue(node.energy) > 0,
    );

    // In PKR each slice is costed at its own category's rate, so a genset or
    // solar branch contributes its real share of spend rather than its share
    // of kWh priced at the grid tariff.
    const series = items.map((node) => {
      const energy = parseValue(node.energy);
      return isPkr ? energy * rateForCategory(node.category, rates) : energy;
    });
    const labels = items.map((node) => node.machine_name);
    // Use assigned machine color → node.color fallback → palette fallback
    const colors = items.map((node, idx) =>
      getMachineColor(node.machine_name, machineColors, idx) ||
      node.color ||
      defaultPalette[idx % defaultPalette.length]
    );

    return { series, labels, colors, items };
  }, [currentView, hierarchyData, machineColors, isPkr, rates]);

  const handleDataPointSelection = (event, chartContext, config) => {
    const index = config.dataPointIndex;
    if (index < 0 || !chartData.items[index]) return;

    const selectedNode = chartData.items[index];

    const directChildren = selectedNode?.children || [];
    const extraChildren = Object.values(hierarchyData || {}).filter(
      (n) =>
        n?.meter_id &&
        selectedNode?.meter_id &&
        Array.isArray(n?.parent_meter_ids) &&
        n.parent_meter_ids.includes(selectedNode.meter_id) &&
        !directChildren.some((dc) => dc.meter_id === n.meter_id),
    );

    const allChildren = [...directChildren, ...extraChildren];

    if (allChildren.length > 0) {
      setHistory((prev) => [...prev, currentView]);

      setCurrentView({
        name: selectedNode.machine_name,
        meter_id: selectedNode.meter_id,
        children: allChildren,
      });

      setBreadcrumbPath((prev) => [
        ...prev,
        {
          label: selectedNode.machine_name,
          onClick: () => navigateToBreadcrumb(history.length + 1),
        },
      ]);
    }
  };

  const navigateToBreadcrumb = (targetIndex) => {
    if (targetIndex === 0) {
      resetToRoot();
      return;
    }
    const newHistory = history.slice(0, targetIndex);
    const targetView = history[targetIndex - 1];

    setHistory(newHistory);
    setCurrentView(targetView);
    setBreadcrumbPath((prev) => prev.slice(0, targetIndex + 1));
  };

  const handleBack = () => {
    if (!history.length) return;
    const newHistory = [...history];
    const previousView = newHistory.pop();

    setHistory(newHistory);
    setCurrentView(previousView);
    setBreadcrumbPath((prev) => prev.slice(0, -1));
  };

  const chartOptions = {
    chart: {
      type: "pie",
      events: { dataPointSelection: handleDataPointSelection },
    },
    labels: chartData.labels,
    colors: chartData.colors,
    title: {
      text: currentView
        ? `${hierarchyData[currentView.name]?.machine_name || currentView.name} Breakdown`
        : "Energy Breakdown",
      align: "center",
      style: { fontSize: "16px", fontWeight: 600 },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: { formatter: (value) => `${formatAmount(value)} ${valueUnit}` },
    },
    legend: { position: "bottom" },
  };

  if (loading)
    return (
      <div
        style={{
          position: "relative",
          minHeight: 350,
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        }}
      >
        <BoltLoader label="Loading Energy Breakdown…" />
      </div>
    );
  if (error)
    return (
      <div style={{ textAlign: "center", padding: 20, color: "red" }}>
        {error}
      </div>
    );
  if (!hierarchyData) return null;

  return (
    <div
      ref={chartRef}
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: 20,
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
        minHeight: 400,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>
          {isPkr ? "Cost Breakdown" : "Energy Breakdown"}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {history.length > 0 && <button onClick={handleBack} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#f8fafc", cursor: "pointer", fontSize: 12 }}>← Back</button>}
          <AIAnalyzerButton onClick={handleAnalyzePieChart} />
          <ChartBurgerMenu
            chartRef={chartRef}
            chartTitle={isPkr ? "Cost_Breakdown" : "Energy_Breakdown"}
            onAIAnalyze={handleAnalyzePieChart}
            chartData={chartData}
            exportContainer={true}
            chartType="pie"
          />
        </div>
      </div>

      <Breadcrumb path={breadcrumbPath} />

      {chartData.series.length > 0 ? (
        <Chart
          options={chartOptions}
          series={chartData.series}
          type="pie"
          height={350}
        />
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          No further breakdown available.
        </div>
      )}
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

export default DrillDownPieChart;
